import React, { useEffect, useRef, useState } from 'react';
import './HeroSection.css';
import logoMark from '../../images/logo-mark.png';
import farmerimage from '../../images/farmerimage.png';
import appInfo from '../../constants/appInfo';

/* ============================================================
   Dhenika — Hero Section
   ------------------------------------------------------------
   Design brief this was built against:
   Dhenika is a livestock record-keeper for dairy farmers — every
   animal has an ear-tag ID (AM4545, GJ-04…), a health record, a
   vaccination schedule, a breeding log. The hero should feel like
   that: paper farm records becoming digital, not a generic SaaS
   product page.

   Signature move: small "record tags" (real content — an animal
   ID, a vaccination status, an overdue heat-check) drift up out
   of the phone the way a paper ear-tag would be clipped onto an
   animal — the literal act of a record being tagged and tracked.
   Stat cards count up on load, the phone answers mouse movement
   with a light parallax tilt, and everything else stays quiet
   so that one idea can carry the page.
   ============================================================ */

// Hero-specific copy stays local; the Play Store link now comes
// from the shared constants/appInfo.js file (single source of truth).
const heroDescription =
  "Log vaccinations, breeding, and health records for every animal on your farm — so you always know who needs attention, and when.";

const HEADLINE_LINE_1 = ["Your", "herd's", "whole"];
const HEADLINE_LINE_2 = ["story,"];
const HEADLINE_ACCENT = ["in", "your", "pocket."];

const HERO_FEATURES = [
  { icon: '💉', label: 'Vaccination reminders' },
  { icon: '🩺', label: 'Health records' },
  { icon: '🧬', label: 'Breeding tracker' },
];

const STATS = [
  { key: 'total', label: 'Total', icon: '🐄', value: 4 },
  { key: 'pregnant', label: 'Pregnant', icon: '🤰', value: 0 },
  { key: 'sick', label: 'Sick', icon: '🏥', value: 1 },
  { key: 'healthy', label: 'Healthy', icon: '✅', value: 2 },
];

const QUICK_TASKS = [
  { icon: '💉', title: 'Vaccination', desc: 'Select cow → log vaccine', tone: 'blue' },
  { icon: '🩺', title: 'Medical record', desc: 'Select cow → log illness', tone: 'green' },
  { icon: '🐄', title: 'Add cow', desc: 'Register a new animal', tone: 'yellow' },
  { icon: '🐛', title: 'Deworming', desc: 'Select cow → schedule', tone: 'purple' },
];

const FLOAT_TAGS = [
  { id: 'am4545', text: 'AM4545', sub: 'Healthy', tone: 'green', pos: { top: '4%', left: '-11%' }, delay: 0, duration: 7 },
  { id: 'vacc', text: 'AM3433', sub: 'Vaccinated ✓', tone: 'green', pos: { top: '20%', right: '-15%' }, delay: 1.4, duration: 8 },
  { id: 'mh04', text: 'MH-04', sub: 'Ear tag synced', tone: 'ochre', pos: { top: '52%', left: '-16%' }, delay: 3, duration: 7.5 },
  { id: 'calv', text: 'Next calving', sub: 'in 12 days', tone: 'ochre', pos: { top: '46%', right: '-12%' }, delay: 2.1, duration: 6.8 },
  { id: 'Guari', text: 'Guari', sub: 'Heat check overdue', tone: 'red', pos: { top: '78%', left: '-9%' }, delay: 4.2, duration: 7.2 },
];

function useMountedReveal() {
  const [mounted, setMounted] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = (e) => setReducedMotion(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);

    const raf = requestAnimationFrame(() => setMounted(true));
    return () => {
      cancelAnimationFrame(raf);
      mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
    };
  }, []);

  return { mounted, reducedMotion };
}

function useCountUp(targets, ready, reducedMotion, baseDelay = 500) {
  const [counts, setCounts] = useState(targets.map(() => 0));

  useEffect(() => {
    if (!ready) return;
    if (reducedMotion) {
      setCounts(targets);
      return;
    }
    const timers = targets.map((target, i) => {
      return setTimeout(() => {
        const duration = 650;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCounts((prev) => {
            const next = [...prev];
            next[i] = Math.round(eased * target);
            return next;
          });
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }, baseDelay + i * 130);
    });
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, reducedMotion]);

  return counts;
}

function PhoneMockup({ mounted, reducedMotion }) {
  const counts = useCountUp(STATS.map((s) => s.value), mounted, reducedMotion, 550);

  return (
    <div className="jvh-phone" aria-hidden="true">
      <div className="jvh-phone__notch" />
      <div className="jvh-phone__screen">
        <div className="jvh-phone__statusbar">
          <span>1:49</span>
          <span className="jvh-phone__statusicons">●●● 5G 58%</span>
        </div>

        <div className="jvh-phone__appbar">
          <div className="jvh-phone__brand">
            <span className="jvh-phone__mark">
              <img src={logoMark} alt="" className="jvh-phone__mark-img" />
            </span>
            <div>
              <div className="jvh-phone__wordmark">Dhenika</div>
              <div className="jvh-phone__submark">Shinde's Farm</div>
            </div>
          </div>
          <img src={farmerimage} alt="" className="jvh-phone__avatar" />
        </div>

        <div className="jvh-phone__stats">
          {STATS.map((stat, i) => (
            <div className="jvh-phone__stat" key={stat.key}>
              <div className="jvh-phone__stat-value">{counts[i]}</div>
              <div className="jvh-phone__stat-icon">{stat.icon}</div>
              <div className="jvh-phone__stat-label">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="jvh-phone__section-label">
          <span>Today's reminders</span>
          <span className="jvh-phone__link">See all</span>
        </div>

        <div className="jvh-phone__reminder">
          <span className="jvh-phone__reminder-dot" />
          <div className="jvh-phone__reminder-body">
            <div className="jvh-phone__reminder-name">Guari</div>
            <div className="jvh-phone__reminder-task">🌡️ Heat Check</div>
          </div>
          <span className="jvh-phone__reminder-pill">Overdue</span>
        </div>

        <div className="jvh-phone__section-label">
          <span>Quick tasks</span>
        </div>

        <div className="jvh-phone__tasks">
          {QUICK_TASKS.map((task) => (
            <div className={`jvh-phone__task jvh-phone__task--${task.tone}`} key={task.title}>
              <div className="jvh-phone__task-icon">{task.icon}</div>
              <div className="jvh-phone__task-title">{task.title}</div>
              <div className="jvh-phone__task-desc">{task.desc}</div>
            </div>
          ))}
        </div>

        <div className="jvh-phone__task jvh-phone__task--pink jvh-phone__task--wide">
          <div className="jvh-phone__task-icon">🧬</div>
          <div>
            <div className="jvh-phone__task-title">Add breeding</div>
            <div className="jvh-phone__task-desc">Log a breeding event</div>
          </div>
        </div>

        <div className="jvh-phone__navbar">
          <div className="jvh-phone__navitem jvh-phone__navitem--active">
            <span>🏠</span>Home
          </div>
          <div className="jvh-phone__navitem"><span>🐄</span>Cows</div>
          <div className="jvh-phone__navitem"><span>🔔</span>Alerts</div>
          <div className="jvh-phone__navitem"><span>🩺</span>Doctor</div>
          <div className="jvh-phone__navitem"><span>👤</span>Profile</div>
        </div>
      </div>
    </div>
  );
}

function FloatingTags({ reducedMotion }) {
  return (
    <div className="jvh-tagfield" aria-hidden="true">
      {FLOAT_TAGS.map((tag) => (
        <div
          className={`jvh-tag jvh-tag--${tag.tone}`}
          key={tag.id}
          style={{
            ...tag.pos,
            animationDelay: reducedMotion ? undefined : `${tag.delay}s`,
            animationDuration: reducedMotion ? undefined : `${tag.duration}s`,
            animationPlayState: reducedMotion ? 'paused' : 'running',
          }}
        >
          <span className="jvh-tag__notch" />
          <span className="jvh-tag__text">{tag.text}</span>
          <span className="jvh-tag__sub">{tag.sub}</span>
        </div>
      ))}
    </div>
  );
}

export default function HeroSection() {
  const { mounted, reducedMotion } = useMountedReveal();
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const stageRef = useRef(null);

  useEffect(() => {
    if (document.getElementById('jvh-fonts')) return;
    const link = document.createElement('link');
    link.id = 'jvh-fonts';
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,600..900;1,9..144,600&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500&display=swap';
    document.head.appendChild(link);
  }, []);

  const handleMouseMove = (e) => {
    if (reducedMotion || !stageRef.current) return;
    const rect = stageRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: x * 8, y: y * -8 });
  };
  const handleMouseLeave = () => setTilt({ x: 0, y: 0 });

  let wordIndex = 0;

  return (
    <section className={`jvh-hero${mounted ? ' is-mounted' : ''}`} id="hero">
      <div className="jvh-hero__field-lines" aria-hidden="true" />
      <div className="jvh-hero__blob jvh-hero__blob--1" aria-hidden="true" />
      <div className="jvh-hero__blob jvh-hero__blob--2" aria-hidden="true" />

      <div className="jvh-hero__container">
        <div className="jvh-hero__content">
          <span className="jvh-hero__eartag" style={{ transitionDelay: '0.05s' }}>
            <span className="jvh-hero__eartag-notch" aria-hidden="true" />
            Digital ear tag · Livestock records
          </span>

          <h1 className="jvh-hero__heading">
            <span className="jvh-hero__heading-line">
              {HEADLINE_LINE_1.map((word) => {
                wordIndex += 1;
                return (
                  <span className="jvh-word" key={word} style={{ transitionDelay: `${0.12 + wordIndex * 0.05}s` }}>
                    {word}
                  </span>
                );
              })}
            </span>
            <span className="jvh-hero__heading-line">
              {HEADLINE_LINE_2.map((word) => {
                wordIndex += 1;
                return (
                  <span className="jvh-word" key={word} style={{ transitionDelay: `${0.12 + wordIndex * 0.05}s` }}>
                    {word}
                  </span>
                );
              })}
            </span>
            <span className="jvh-hero__heading-line jvh-hero__heading--accent">
              {HEADLINE_ACCENT.map((word) => {
                wordIndex += 1;
                return (
                  <span className="jvh-word" key={word} style={{ transitionDelay: `${0.12 + wordIndex * 0.05}s` }}>
                    {word}
                  </span>
                );
              })}
            </span>
          </h1>

          <p className="jvh-hero__description" style={{ transitionDelay: '0.5s' }}>
            {heroDescription}
          </p>

          <div className="jvh-hero__cta" style={{ transitionDelay: '0.6s' }}>
            <a
              href={appInfo.playStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="jvh-hero__store-btn jvh-hero__store-btn--play"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M3.6 2.3c-.4.4-.6.9-.6 1.6v16.2c0 .7.2 1.2.6 1.6l.1.1L13 12.5v-.1L3.7 2.2l-.1.1z" />
                <path d="M16.3 15.8 13 12.5v-.1l3.3-3.3 7.4 4.2c.6.4.9.9.9 1.4s-.3 1-1 1.4l-7.3 4.2v-.1z" opacity=".55" />
                <path d="M16.3 8.8 13 12.1 3.7 2.9c.3-.3.7-.4 1.1-.2l11.5 6.1z" opacity=".8" />
                <path d="M16.3 15.8 4.8 21.9c-.4.2-.8.1-1.1-.2l9.3-9.2 3.3 3.3z" />
              </svg>
              <span className="jvh-hero__store-text">
                <span className="jvh-hero__store-sub">Get it on</span>
                <span className="jvh-hero__store-name">Google Play</span>
              </span>
            </a>
          </div>

          <ul className="jvh-hero__feature-tags" style={{ transitionDelay: '0.7s' }}>
            {HERO_FEATURES.map((feature) => (
              <li className="jvh-hero__feature-tag" key={feature.label}>
                <span className="jvh-hero__feature-tag-icon" aria-hidden="true">{feature.icon}</span>
                {feature.label}
              </li>
            ))}
          </ul>
        </div>

        <div
          className="jvh-hero__visual"
          ref={stageRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{ transitionDelay: '0.25s' }}
        >
          <div className="jvh-hero__phone-glow" aria-hidden="true" />
          <FloatingTags reducedMotion={reducedMotion} />
          <div
            className="jvh-phone-wrapper"
            style={{
              transform: reducedMotion
                ? undefined
                : `perspective(1200px) rotateY(${tilt.x}deg) rotateX(${tilt.y}deg)`,
            }}
          >
            <PhoneMockup mounted={mounted} reducedMotion={reducedMotion} />
          </div>
        </div>
      </div>
    </section>
  );
}